import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import {
  AiFeaturesService,
  GenerateIqBoostRequest,
  IqBoostPrompt,
  IQBoostViewStore,
  LANGFUSE_SYSTEM_TAG,
  LanguageHelper,
  MaximizerAiService,
  SYSTEM_PROMPT_DETAILED_REPORT_INSTRUCTIONS,
  ViewType,
} from '@maximizer/ai/data-access';
import { OpenAiService } from '@maximizer/core/shared/data-access';
import { CDN_URL } from '@maximizer/core/shared/domain';
import { DisposableComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import {
  Observable,
  catchError,
  finalize,
  map,
  of,
  reduce,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import {
  EventAction,
  EventJourney,
  IQBoostEventTracker,
} from '../classes/event-tracker';
import { RootObject } from '../classes/iqboost-report';
import { FeedbackComponent } from '../feedback/feedback.component';
import { ClickableRefPipe } from '../pipes/clickable-ref.pipe';
import { CreateCRMObjectPipe } from '../pipes/create-crm-object.pipe';
import { DeAnonymizerPipe } from '../pipes/de-anonymizer.pipe';
import { EntityKeyRefPipe } from '../pipes/entity-key-ref.pipe';
import { SafeHtmlPipe } from '../pipes/safe-html.pipe';
import { WebSearchRefPipe } from '../pipes/web-search-ref.pipe';
import { removeMismatchedClosingBrackets } from '../utils/markup-string-scan.util';

/** Card types the template has a branch for; a section with anything else paints nothing. */
const RENDERABLE_CARD_TYPES: readonly string[] = [
  'list-card',
  'text-card',
  'value-card',
  'progress',
];

@Component({
  selector: 'ai-iqboost-report',
  templateUrl: './iqboost-report.component.html',
  imports: [
    ClickableRefPipe,
    CreateCRMObjectPipe,
    DeAnonymizerPipe,
    EntityKeyRefPipe,
    FeedbackComponent,
    KENDO_BUTTON,
    NgTemplateOutlet,
    NgClass,
    NgStyle,
    SafeHtmlPipe,
    WebSearchRefPipe,
    TranslatePipe,
    UI_PIPES,
  ],
})
export class IqboostReportComponent
  extends DisposableComponent
  implements OnInit, AfterViewInit
{
  @ViewChild('feedback') feedback?: FeedbackComponent;

  ViewType = ViewType;

  aiReportData = signal({} as RootObject);
  aiResponseJsonString = computed(() => JSON.stringify(this.aiReportData()));

  /** Resolved on the first generation and reused by Regenerate. */
  private readonly instructions = signal<string[] | undefined>(undefined);

  constructor(
    public readonly tracker: IQBoostEventTracker,
    public readonly store: IQBoostViewStore,
    public readonly openAiService: OpenAiService,
    private readonly maximizerAi: MaximizerAiService,
    private readonly aiFeatures: AiFeaturesService,
    @Inject(CDN_URL) readonly cdn: string,
  ) {
    super();
  }

  ngOnInit(): void {
    this.generateReport();
  }

  ngAfterViewInit(): void {
    if (this.feedback) {
      this.feedback.disabled.set(
        this.store.getFeedbackDisabled(this.aiResponseJsonString())
      );
    }
  }

  regenerateReport(): void {
    this.aiReportData.set({} as RootObject);
    this.feedback?.disabled.set(false);
    // Regenerate must not replay the cached answer the user just rejected.
    this.generateReport(true);
    this.tracker.trackEvent(
      EventJourney.Prompt, 
      EventAction.Clicked, 
      {
        name: 'generate-detailed-report',
        prompt: this.store.currentPrompt()?.name ?? '',
      });
  }

  onBack(): void {
    this.store.setViewType(ViewType.Report);
  }

  getPromptName(prompt?: IqBoostPrompt): string {
    return prompt?.title ?? '';
  }

  /**
   * The detailed report is its own generate-response call: the selected prompt plus the system
   * instruction that pins the JSON layout this view renders. That layout is unreadable until its
   * last token, so the stream is accumulated and parsed once, when it completes.
   */
  private generateReport(invalidateCache = false): void {
    const request = this.buildRequest(invalidateCache);

    if (!request) {
      // Nothing to ask for; do not leave the view spinning.
      this.store.setLoading(false);
      return;
    }

    this.store.setLoading(true);

    this.addSubscription(
      this.getInstructions()
        .pipe(
          switchMap((system) =>
            system.length
              ? this.maximizerAi.generateResponse({
                  ...request,
                  additionalMessages: { system },
                })
              // Without the instruction this is the plain summary request, which can never
              // return the layout, so fail instead of asking for the wrong thing.
              : throwError(() => new Error('Missing the detailed report instruction prompt')),
          ),
          // Phase events are the only progress the user gets until the layout is complete.
          tap((chunk) => {
            if (chunk.phase !== undefined) {
              this.store.setStreamingPhase(chunk.phase);
            }
          }),
          reduce((json, chunk) => json + (chunk.content ?? ''), ''),
          tap((json) => this.setReportData(json)),
          catchError((error) => {
            console.error(error);
            this.store.setShowErrorMessageDialog(true);
            return of(undefined);
          }),
          // Cancelling tears the stream down instead of completing it, so reduce never emits
          // and a half-received layout is never parsed.
          takeUntil(this.store.cancelInFlightSubject()),
          finalize(() => {
            this.store.setStreamingPhase(null);
            this.store.setLoading(false);
          }),
        )
        .subscribe(),
    );
  }

  private buildRequest(invalidateCache: boolean): GenerateIqBoostRequest | undefined {
    const promptName = this.store.currentPrompt()?.name ?? '';
    const entityKey = this.store.key() ?? '';

    if (isNullOrEmpty(promptName) || isNullOrEmpty(entityKey)) {
      return undefined;
    }

    return {
      entityKey,
      promptName,
      entityType: this.store.type() ?? 'addressBook',
      language: LanguageHelper.language(),
      invalidateCache,
    };
  }

  /** Prompt bodies never change within a session; resolve them once per visit to this view. */
  private getInstructions(): Observable<string[]> {
    const cached = this.instructions();

    if (cached?.length) {
      return of(cached);
    }

    return this.maximizerAi
      .lookupPrompts({
        project: this.aiFeatures.isSLE() ? 'IqBoostSLE' : 'IqBoost',
        filter: {
          tag: [LANGFUSE_SYSTEM_TAG],
          name: [SYSTEM_PROMPT_DETAILED_REPORT_INSTRUCTIONS],
        },
      })
      .pipe(
        map((prompts) =>
          prompts
            .map((prompt) => prompt.prompt)
            .filter((prompt) => !isNullOrEmpty(prompt)),
        ),
        // Only cache a real instruction: caching an empty result would pin the failure.
        tap((instructions) => {
          if (instructions.length) {
            this.instructions.set(instructions);
          }
        }),
      );
  }

  private setReportData(aiResponseJsonString: string): void {
    const report = this.parseReport(aiResponseJsonString);

    this.aiReportData.set(report ?? ({} as RootObject));

    if (!report) {
      this.store.setShowErrorMessageDialog(true);
    }
  }

  /** tryParseJson throws when the response drifts from the layout contract, which would kill the subscription. */
  private parseReport(aiResponseJsonString: string): RootObject | undefined {
    try {
      const sanitized = this.sanitizeReportJson(aiResponseJsonString);
      const parsed = this.openAiService.tryParseJson<RootObject>(sanitized);
      // Sections the template has no branch for render nothing, so a report made only of
      // those is a failed response rather than an empty one.
      const renderable = parsed?.sections?.some((section) =>
        RENDERABLE_CARD_TYPES.includes(section?.cardType),
      );

      return renderable ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  private sanitizeReportJson(raw: string): string {
    return removeMismatchedClosingBrackets(
      this.escapeAnonymizedAttributeQuotes(raw),
    );
  }

  /**
   * Server-injected PII tags look like `<anonymized value="Name"/>` but their attribute
   * quotes are not JSON-escaped, which breaks JSON.parse. Escape them so the parsed
   * string still contains the tag the deAnonymizer pipe expects. Also accepts `""…""`
   * doubling that sometimes appears in the same payloads.
   */
  private escapeAnonymizedAttributeQuotes(raw: string): string {
    if (!raw.includes('anonymized value=')) {
      return raw;
    }

    return raw.replace(
      /(<|&lt;)anonymized\s+value=(""|")([^"]*)\2\s*\/?(>|&gt;)/gi,
      (_match, open: string, _quotes: string, value: string, close: string) =>
        String.raw`${open}anonymized value=\"${value}\"/${close}`,
    );
  }
}
